import { useEffect, useLayoutEffect } from 'react';
import { checkForWrongDomain } from '@/utils/checkForWrongDomain';
import Layout from '@/components/Layout';
import ProductDescription from '../../../../containers/ProductDescription';
import { getCatalogItemById } from '../../../../services';
import { _404Redirect } from '@/utils/404Redirect';
import { getSSRStoreData } from '../../../../utils/ssrPageData';
import { useSetSSRStore, useSSRSelector } from '../../../../redux';
import { getRequiredCtxProps } from 'src/utils/getRequiredCtxProps';
import { pushFeatureLockCollectiveData } from 'src/utils/hack.featureLocks';
import { handlePDPErrorCaseRedirection } from 'src/utils/productUtils';
import { generateOneTimeTokenSEO, getCachedHTML, saveHTMLData } from 'src/services/seo';
import { GetServerSidePropsContext } from 'next';
import { isbot } from "isbot";

const ProductDescPage = (props) => {
  useSetSSRStore(props.ssrStore);
  const storeInfo = useSSRSelector((state) => state.storeReducer.store);
  const loading = useSSRSelector((state) => state.commonReducer.loader);
  const suggestions = useSSRSelector((state) => state.catalogReducer.sharePDP.suggestions);


  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  function handleLoad() {
    
    if (suggestions?.length && props.token && typeof window !== 'undefined') {
      const html = document.documentElement.outerHTML;
      if(!html) return null
      const url = (location.origin + location.pathname).toLowerCase();
      const body = { html, url };
      saveHTMLData(body, props.token);
    }
  }

  useEffect(() => {
    handleLoad()
  }, [loading, suggestions, props.token]);


  if (props.cache) return null

  return (
    <Layout
      store={storeInfo}
      title={
        props?.advCustomSeoDetails?.title?.value
          ? props?.advCustomSeoDetails?.title?.value
          : `Order ${props?.product?.name} Online From ${storeInfo?.store_info?.name}${storeInfo?.address?.visibility === 1 && storeInfo.address?.city
            ? `,${storeInfo.address?.city}`
            : ``
          }`
      }
      OGTitle={props?.advCustomSeoDetails?.og_title?.value || null}
      OGDescription={props?.advCustomSeoDetails?.og_description || null}
      Icon={props?.product?.image_url}
      isCustomDomain={props.isCustomDomain}
      metaDescription={
        props?.advCustomSeoDetails?.description
          ? props?.advCustomSeoDetails?.description
          : `Order Online ${props?.product?.name} for Rs.${props?.product?.price}. Shop from ${storeInfo?.store_info?.name},${storeInfo.address?.city} | ${props?.product?.description}`
      }
      canonicalTagUrl={props?.product?.link}
      {...props}
    >
      <ProductDescription {...props} />
    </Layout>
  );
};

export async function getServerSideProps(ctx) {
  if (checkForWrongDomain(ctx)) return {};
  const userAgent = ctx.req.headers['user-agent'] || '';
  const isCrawler = isbot(userAgent);
  let token = null

  if (isCrawler) {
    await redirectToCache(ctx);
  } else {
    const tokenData = await generateOneTimeTokenSEO();
    if (tokenData?.data?.text) {
      token = tokenData.data.text;
    }
  }

  try {
    const { pid } = ctx.query;
    let propData = {
      product: {},
      catalogCategories: [],
      catalogTags: [],
      collections: [],
      advCustomSeoDetails: {},
      token: null
    };

    // to check only for number string
    const regExp = /[a-zA-Z]/g;
    if (regExp.test(pid)) {
      ctx.res.writeHead(307, { location: '/404' });
      return { props: {} };
    }

    const propType = await getSSRStoreData(ctx, {
      additionalDataToFetch: ['collections', 'categories', 'advancedSeo'],
      advancedSeoReqConfigs: {
        type: 'item',
        typeId: pid,
      },
    });

    /**
     * This was a hack to retry fetching the required SSR data if it is not there.
     * discuss with respective devs before making any changes here or reordering this peice of code.
     */
    const { advCustomSeoDetails } = await pushFeatureLockCollectiveData({
      storeInfo: propType?.ssrStore?.storeReducer?.store,
      advancedSeoReqConfigs: {
        type: 'item',
        typeId: pid,
      },
      additionalDataToFetch: ['collections', 'categories', 'advancedSeo'],
      propType,
      ctx,
    });

    const storeId = propType?.ssrStore?.storeReducer?.store?.store_id;

    if (!storeId) {
      _404Redirect(ctx, 302);
      ctx.res.end();
      return { props: {} };
    }

    const productResponse = await getCatalogItemById(storeId, pid);

    if (productResponse.data && productResponse.data.status) {
      const productItem = JSON.parse(productResponse?.data?.data);
      propData.product = productItem;
    } else {
      handlePDPErrorCaseRedirection({ ctx, productResponse, propType });
      ctx.res.end();
      return { props: {} };
    }

    const extraProps = getRequiredCtxProps(ctx, propType);
    propData = { ...propData, ...propType };

    return { props: { ...propData, ...extraProps, advCustomSeoDetails, token } };

  } catch (err) {
    if (isCrawler) {
      return await redirectToCache(ctx);
    } else {
      _404Redirect(ctx, 302);
      ctx.res.end();
      return { props: {} };
    }
  }
}

export default ProductDescPage;


async function redirectToCache(ctx: GetServerSidePropsContext) {
  const cleanUrl = `${ctx.req.headers.host}${ctx.req.url?.split('?')[0]}`;
  if (cleanUrl) {
    const htmlData = await getCachedHTML(cleanUrl.toLowerCase());

    if (htmlData?.data) {
      ctx.res.setHeader('Content-Type', 'text/html');
      ctx.res.write(htmlData.data);
      ctx.res.end();
      // fallback
      return { props: { cache: true } };
    }
  }
}

